import * as THREE from 'three';

/** Euler rotation (radians, XYZ order) for one joint. */
export type Rot = [number, number, number];

/** A (partial) pose: joint rotations plus root offsets. Missing joints fall through to lower layers. */
export interface Pose {
  joints: Partial<Record<string, Rot>>;
  /** Root offsets in the character's local frame (metres / radians). */
  rootY?: number;
  rootZ?: number;
  rootX?: number;
  rootYaw?: number;
  rootPitch?: number;
  rootRoll?: number;
}

export const ROOT_KEYS = ['rootY', 'rootZ', 'rootX', 'rootYaw', 'rootPitch', 'rootRoll'] as const;
export type RootKey = (typeof ROOT_KEYS)[number];

const qa = new THREE.Quaternion();
const qb = new THREE.Quaternion();
const ea = new THREE.Euler();

function rotToQuat(r: Rot, out: THREE.Quaternion): THREE.Quaternion {
  ea.set(r[0], r[1], r[2], 'XYZ');
  return out.setFromEuler(ea);
}

/** Resolved pose: one quaternion per joint, used for blending. */
export class QPose {
  joints = new Map<string, THREE.Quaternion>();
  root: Record<RootKey, number> = { rootY: 0, rootZ: 0, rootX: 0, rootYaw: 0, rootPitch: 0, rootRoll: 0 };

  static fromPose(p: Pose, jointNames: readonly string[], base?: QPose): QPose {
    const q = new QPose();
    for (const j of jointNames) {
      const r = p.joints[j];
      if (r) q.joints.set(j, rotToQuat(r, new THREE.Quaternion()));
      else q.joints.set(j, base?.joints.get(j)?.clone() ?? new THREE.Quaternion());
    }
    for (const k of ROOT_KEYS) q.root[k] = p[k] ?? base?.root[k] ?? 0;
    return q;
  }

  copy(o: QPose): this {
    for (const [j, v] of o.joints) {
      const mine = this.joints.get(j);
      if (mine) mine.copy(v);
      else this.joints.set(j, v.clone());
    }
    this.root = { ...o.root };
    return this;
  }

  /** In-place blend towards `o` by weight `t`. */
  blend(o: QPose, t: number): this {
    if (t <= 0) return this;
    for (const [j, v] of o.joints) {
      const mine = this.joints.get(j);
      if (mine) mine.slerp(v, Math.min(1, t));
      else this.joints.set(j, v.clone());
    }
    for (const k of ROOT_KEYS) this.root[k] += (o.root[k] - this.root[k]) * Math.min(1, t);
    return this;
  }
}

/** Overlays a partial pose onto a resolved pose with weight `w` (only joints present in `p`). */
export function overlay(target: QPose, p: Pose, w: number): void {
  if (w <= 0) return;
  for (const [j, r] of Object.entries(p.joints)) {
    if (!r) continue;
    rotToQuat(r, qb);
    const cur = target.joints.get(j);
    if (cur) cur.slerp(qb, Math.min(1, w));
  }
  for (const k of ROOT_KEYS) {
    const v = p[k];
    if (v !== undefined) target.root[k] += (v - target.root[k]) * Math.min(1, w);
  }
}

/** Adds an additive rotation (e.g. hit flinch) on top of the current pose. */
export function additive(target: QPose, p: Pose, w: number): void {
  if (w <= 0) return;
  for (const [j, r] of Object.entries(p.joints)) {
    if (!r) continue;
    rotToQuat([r[0] * w, r[1] * w, r[2] * w], qa);
    const cur = target.joints.get(j);
    if (cur) cur.multiply(qa);
  }
  for (const k of ROOT_KEYS) {
    const v = p[k];
    if (v !== undefined) target.root[k] += v * w;
  }
}

// ---------------------------------------------------------------- keyframed clips

export interface Key {
  t: number;
  pose: Pose;
}

export interface Clip {
  duration: number;
  keys: Key[];
  /** Hold the last key instead of finishing (death). */
  hold?: boolean;
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/** Samples a keyframed clip at normalised time u (0..1) into a partial Pose (linear-in-euler with easing). */
export function sampleClip(clip: Clip, u: number): Pose {
  const keys = clip.keys;
  const first = keys[0] as Key;
  if (u <= first.t) return first.pose;
  const last = keys[keys.length - 1] as Key;
  if (u >= last.t) return last.pose;
  let i = 0;
  while (i < keys.length - 1 && (keys[i + 1] as Key).t < u) i++;
  const a = keys[i] as Key;
  const b = keys[i + 1] as Key;
  const k = easeInOut((u - a.t) / Math.max(1e-6, b.t - a.t));
  const out: Pose = { joints: {} };
  const names = new Set([...Object.keys(a.pose.joints), ...Object.keys(b.pose.joints)]);
  for (const n of names) {
    const ra = a.pose.joints[n] ?? b.pose.joints[n] ?? [0, 0, 0];
    const rb = b.pose.joints[n] ?? ra;
    out.joints[n] = [ra[0] + (rb[0] - ra[0]) * k, ra[1] + (rb[1] - ra[1]) * k, ra[2] + (rb[2] - ra[2]) * k];
  }
  for (const rk of ROOT_KEYS) {
    const va = a.pose[rk];
    const vb = b.pose[rk];
    if (va === undefined && vb === undefined) continue;
    const x = va ?? 0;
    const y = vb ?? 0;
    out[rk] = x + (y - x) * k;
  }
  return out;
}
